/**
 * @file Componente Geography do EP.
 * @module components/create/geography/geography
 *
 * Componente composto para País, Estado e Cidade com cascata automática e
 * catálogo geográfico encapsulado pelo próprio EP.
 *
 * @see configureCountryCombo
 */

import append from '../../define/append';
import setAttributes from '../../define/setAttributes';
import setEvents from '../../define/setEvents';
import { configureCountryCombo } from './options';
import { getGeographyOptions } from './data';

/**
 * Metadados canônicos dos níveis suportados pelo Geography.
 *
 * @constant
 */
export const GEOGRAPHY_LEVELS = Object.freeze({
  country: Object.freeze({
    key: 'cb_countries',
    flag: 'countries',
    aliases: ['country', 'countryCode', 'country_code'],
    dependsOn: [],
    clears: ['state', 'city'],
  }),
  state: Object.freeze({
    key: 'cb_states',
    flag: 'states',
    aliases: ['state', 'stateCode', 'state_code', 'uf'],
    dependsOn: ['country'],
    clears: ['city'],
  }),
  city: Object.freeze({
    key: 'cb_cities',
    flag: 'cities',
    aliases: ['city', 'cityId', 'city_id'],
    dependsOn: ['country', 'state'],
    clears: [],
  }),
});

/** @constant {Array<'country'|'state'|'city'>} */
export const GEOGRAPHY_LEVEL_ORDER = Object.freeze(['country', 'state', 'city']);
let geographySequence = 0;

/**
 * Normaliza texto para comparações tolerantes a acento e caixa.
 *
 * @param {*} value Valor de entrada.
 * @returns {string} Texto normalizado.
 */
const normalizeText = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLocaleLowerCase();

/**
 * Normaliza uma chave removendo caracteres não alfanuméricos.
 *
 * @param {*} value Valor de entrada.
 * @returns {string} Chave normalizada.
 */
const normalizeKey = (value) => String(value ?? '')
  .trim()
  .replace(/[^a-zA-Z0-9]+/g, '')
  .toLocaleLowerCase();

/**
 * Indica se um valor é semanticamente preenchido para o Geography.
 *
 * @param {*} value Valor testado.
 * @returns {boolean} `true` quando existe conteúdo útil.
 */
const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

/**
 * Lê o primeiro valor presente entre chaves/aliases, incluindo a parte final de
 * nomes qualificados por ponto.
 *
 * @param {Object<string,*>} source Objeto de valores.
 * @param {string[]} [keys=[]] Chaves candidatas.
 * @returns {*} Valor encontrado ou `undefined`.
 */
const readValue = (source, keys = []) => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return undefined;

  for (const key of keys) {
    const raw = String(key || '').trim();
    if (!raw) continue;
    if (Object.prototype.hasOwnProperty.call(source, raw)) return source[raw];

    const short = raw.split('.').pop();
    if (short && Object.prototype.hasOwnProperty.call(source, short)) return source[short];
  }

  return undefined;
};

/**
 * Resolve o parent aceito pelo componente.
 *
 * @param {HTMLElement|string} parent Elemento ou seletor CSS.
 * @returns {Element|null} Elemento resolvido.
 */
const resolveParent = (parent) => {
  if (parent instanceof Element) return parent;
  if (typeof parent === 'string') return document.querySelector(parent);
  return null;
};

/**
 * Resolve o label padrão localizado de um nível.
 *
 * @param {'country'|'state'|'city'} level Nível geográfico.
 * @returns {string} Label localizado ou fallback em português.
 */
const defaultLabel = (level) => {
  const labels = globalThis?.dic?.labels || {};
  if (level === 'country') return labels.country || 'País';
  if (level === 'state') return labels.state || labels.uf || 'Estado';
  return labels.city || 'Cidade';
};

/**
 * Resolve um nome ou alias para o nível geográfico canônico.
 *
 * @param {*} candidate Nome/alias candidato.
 * @returns {'country'|'state'|'city'|null} Nível resolvido.
 * @see GEOGRAPHY_LEVELS
 */
export const resolveGeographyLevel = (candidate) => {
  const key = normalizeKey(candidate);
  if (!key) return null;

  return GEOGRAPHY_LEVEL_ORDER.find((level) => {
    const config = GEOGRAPHY_LEVELS[level];
    const candidates = [level, config.flag, ...config.aliases].map(normalizeKey);
    return candidates.includes(key);
  }) || null;
};

/**
 * Monta as opções canônicas do Combo remoto de um nível geográfico.
 *
 * A fonte geográfica é resolvida localmente pelo EP e não exige target remoto.
 *
 * @param {'country'|'state'|'city'} level Nível geográfico.
 * @param {Object} [extra={}] Opções adicionais/sobrescritas do Combo.
 * @returns {Object} Configuração declarativa de `element: 'combo'`.
 * @throws {Error} Para nível inválido.
 */
export const geographyComboOptions = (level, extra = {}) => {
  const config = GEOGRAPHY_LEVELS[level];
  if (!config) throw new Error(`Geography: nível inválido "${level}".`);

  return {
    ...extra,
    element: 'combo',
    key: config.key,
    options: async (comboOptions = {}) => getGeographyOptions(
      level,
      comboOptions
    ),
    filter: extra.filter ?? true,
    size: extra.size ?? 10,
    full: extra.full ?? true,
  };
};

/**
 * Instância runtime do componente Geography.
 *
 * @class
 */
class Geography {
  /**
   * @param {Object} [options={}] Configuração do componente; veja {@link geography}.
   */
  constructor(options = {}) {
    this.options = options || {};
    this.root = null;
    this.group = '';
    this.direction = 'row';
    this.fields = {};
    this.combos = {};
    this.initial = {};
    this.external = {};
    this.cascade = Promise.resolve();
  }

  /**
   * Decide se um nível deve ser renderizado.
   *
   * @param {'country'|'state'|'city'} level Nível consultado.
   * @returns {boolean} Estado de habilitação.
   */
  #isEnabled(level) {
    const key = GEOGRAPHY_LEVELS[level]?.flag;
    if (!key) return false;

    const hasExplicitFlags = GEOGRAPHY_LEVEL_ORDER.some((item) => {
      const flag = GEOGRAPHY_LEVELS[item].flag;
      return Object.prototype.hasOwnProperty.call(this.options, flag);
    });

    if (!hasExplicitFlags) return true;
    return this.options[key] === true;
  }

  /**
   * Normaliza a configuração de um nível, incluindo id, name e defaults globais.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {Object} Configuração normalizada.
   */
  #fieldConfig(level) {
    const raw = this.options?.fields?.[level];
    const field = raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...raw } : {};
    const prefix = String(this.options.name || '').trim();
    const defaultName = prefix ? `${prefix}_${level}` : level;

    return {
      ...field,
      id: String(field.id || `${this.group}-${level}`),
      name: String(field.name || defaultName),
      label: field.label ?? defaultLabel(level),
      disabled: field.disabled ?? this.options.disabled ?? false,
      readOnly: field.readOnly ?? field.readonly ?? this.options.readOnly ?? this.options.readonly ?? false,
    };
  }

  /**
   * Resolve o valor inicial de um nível pela ordem de precedência do componente.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {*} Valor inicial ou `null`.
   */
  #initialValue(level) {
    const field = this.fields[level];
    const levelConfig = GEOGRAPHY_LEVELS[level];
    const keys = [field?.id, field?.name, ...levelConfig.aliases];

    const sources = [
      this.options?.value,
      this.options?.values,
    ];

    for (const source of sources) {
      const value = readValue(source, keys);
      if (hasValue(value)) return value;
    }

    if (hasValue(field?.value)) return field.value;
    if (hasValue(field?.defaultValue)) return field.defaultValue;

    if (level === 'country') {
      if (hasValue(this.options?.defaultValue)) return this.options.defaultValue;
      if (
        !sources.some((source) => source && typeof source === 'object')
        && hasValue(this.options?.value)
      ) {
        return this.options.value;
      }
    }

    return null;
  }

  /**
   * Retorna o valor efetivo de contexto de um nível.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {*} Valor selecionado, externo, inicial ou string vazia.
   */
  #contextValue(level) {
    const combo = this.combos[level];
    if (hasValue(combo?.hidden?.value)) return combo.hidden.value;
    if (hasValue(this.external[level])) return this.external[level];
    if (hasValue(this.initial[level])) return this.initial[level];
    return '';
  }

  /**
   * Retorna o contexto necessário aos combos dependentes.
   *
   * @returns {{country:*,state:*}} País e estado correntes.
   */
  getContextValues() {
    return {
      country: this.#contextValue('country'),
      state: this.#contextValue('state'),
    };
  }

  /**
   * Lista as chaves aceitas no binding de um nível.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {Array<string>} Id, name e aliases.
   */
  #candidateKeys(level) {
    const field = this.fields[level];
    return [field?.id, field?.name, ...GEOGRAPHY_LEVELS[level].aliases];
  }

  /**
   * Localiza uma opção por valor ou label normalizado.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @param {*} candidate Valor candidato.
   * @returns {*|null} Opção encontrada.
   */
  #findOption(level, candidate) {
    const combo = this.combos[level];
    if (!combo || !hasValue(candidate) || !Array.isArray(combo.data)) return null;

    const raw = String(candidate);
    const normalized = normalizeText(candidate);

    const byValue = combo.data.find((option) => {
      if (option && typeof option === 'object') {
        return String(option.value ?? option.id ?? option.code ?? '') === raw;
      }
      return String(option ?? '') === raw;
    });
    if (byValue) return byValue;

    return combo.data.find((option) => {
      if (option && typeof option === 'object') {
        return normalizeText(option.label ?? option.text ?? option.name ?? '') === normalized;
      }
      return normalizeText(option) === normalized;
    }) || null;
  }

  /**
   * Seleciona programaticamente uma opção já carregada.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @param {*} candidate Valor candidato.
   * @returns {boolean} `true` quando a seleção foi aplicada.
   */
  #selectCandidate(level, candidate) {
    const combo = this.combos[level];
    if (!combo || !hasValue(candidate)) return false;

    const option = this.#findOption(level, candidate);
    if (!option) return false;

    const value = option && typeof option === 'object'
      ? String(option.value ?? option.id ?? option.code ?? option.label ?? option.text ?? '')
      : String(option);

    combo.renderData(combo.data, value);
    return String(combo.hidden?.value ?? '') === value;
  }

  /**
   * Limpa seleção e valor interno de um nível renderizado.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {void}
   */
  #clear(level) {
    const combo = this.combos[level];
    if (!combo) return;

    if (combo.hidden) combo.hidden.value = '';
    if (combo.options) combo.options.value = null;
    combo.renderData(Array.isArray(combo.data) ? combo.data : [], null);
  }

  /**
   * Recarrega um Combo remoto com o contexto corrente e tenta selecionar um valor.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @param {*} [preferredValue=null] Valor preferencial.
   * @returns {Promise<void>}
   */
  async #refresh(level, preferredValue = null) {
    const combo = this.combos[level];
    if (!combo?.fill) return;

    if (combo.hidden) combo.hidden.value = '';
    if (combo.options) combo.options.value = null;

    await combo.fill({
      values: this.getContextValues(),
      value: hasValue(preferredValue) ? preferredValue : null,
    });

    if (hasValue(preferredValue) && !hasValue(combo.hidden?.value)) {
      this.#selectCandidate(level, preferredValue);
    }
  }

  /**
   * Executa a cascata dependente de um nível alterado.
   *
   * País limpa/recarrega Estado e Cidade; Estado limpa/recarrega Cidade.
   *
   * @param {'country'|'state'|'city'} level Nível de origem.
   * @returns {Promise<void>}
   */
  async #cascadeFrom(level) {
    if (level === 'country') {
      this.external.state = '';

      if (this.combos.state) {
        this.#clear('state');
        await this.#refresh('state');
      }

      if (this.combos.city) {
        this.#clear('city');
        await this.#refresh('city');
      }
      return;
    }

    if (level === 'state' && this.combos.city) {
      this.#clear('city');
      await this.#refresh('city');
    }
  }

  /**
   * Serializa as cascatas para evitar concorrência entre mudanças rápidas.
   *
   * Após a cascata emite `geographychange` e executa o callback do campo.
   *
   * @param {'country'|'state'|'city'} level Nível alterado.
   * @param {Event} event Evento original.
   * @param {Function} fieldChange Callback configurado no campo.
   * @returns {Promise<*>} Fila corrente da cascata.
   */
  #queueCascade(level, event, fieldChange) {
    this.cascade = this.cascade
      .then(async () => {
        await this.#cascadeFrom(level);

        const detail = {
          group: this.group,
          level,
          value: this.getValue(),
        };
        this.root.dispatchEvent(new CustomEvent('geographychange', {
          bubbles: true,
          detail,
        }));

        if (typeof fieldChange === 'function') {
          await fieldChange(event, detail.value, this.root);
        }
      })
      .catch((error) => {
        console.error(`[geography:${this.group}] falha na cascata ${level}.`, error);
      });

    return this.cascade;
  }

  /**
   * Cria um nível habilitado ou registra seu valor como contexto externo.
   *
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {Promise<void>}
   * @throws {Error} Quando o runtime global `create()` não está disponível.
   */
  async #createLevel(level) {
    if (!this.#isEnabled(level)) {
      this.external[level] = this.initial[level] ?? '';
      return;
    }

    const field = this.fields[level];
    const fieldChange = field.change;
    const initialValue = this.initial[level];
    const create = globalThis.create;

    if (typeof create !== 'function') {
      throw new Error('Geography requer o runtime EP create().');
    }

    await create(geographyComboOptions(level, {
      ...field,
      parent: this.root,
      value: initialValue,
      values: this.getContextValues(),
      data: {
        ...(field.data || {}),
        geographyGroup: this.group,
        geographyLevel: level,
      },
      change: (event) => this.#queueCascade(level, event, fieldChange),
    }));

    const element = document.getElementById(field.id)
      || this.root.querySelector(`[data-geography-level="${level}"]`);
    const combo = element?.combo || null;
    if (!combo) return;

    this.combos[level] = combo;

    if (hasValue(initialValue)) {
      this.#selectCandidate(level, initialValue);
    }

    if (level === 'country') {
      const configured = configureCountryCombo(combo, {
        accept: field.accept ?? this.options.accept,
        reject: field.reject ?? this.options.reject,
        continentGroup: field.continentGroup ?? this.options.continentGroup,
        readOnly: field.readOnly ?? field.readonly ?? this.options.readOnly ?? this.options.readonly,
        disabled: field.disabled ?? this.options.disabled,
      });

      if (hasValue(configured.value)) {
        this.initial.country = configured.value;
      }
    }
  }

  /**
   * Cria sequencialmente todos os níveis conforme a ordem canônica.
   *
   * @returns {Promise<void>}
   * @see GEOGRAPHY_LEVEL_ORDER
   */
  async #createFields() {
    for (const level of GEOGRAPHY_LEVEL_ORDER) {
      await this.#createLevel(level);
    }
  }

  /**
   * Anexa a instância e os atalhos públicos ao elemento raiz.
   *
   * @returns {void}
   */
  #bindApi() {
    this.root.geography = this;
    this.root.getValue = () => this.getValue();
    this.root.setValue = (value, options) => this.setValue(value, options);
    this.root.getField = (level) => this.getField(level);
  }

  /**
   * Retorna o elemento visual de um nível criado.
   *
   * @param {'country'|'state'|'city'} level Nível desejado.
   * @returns {HTMLElement|null} Elemento do Combo ou `null`.
   */
  getField(level) {
    return this.combos[level]?.elem || null;
  }

  /**
   * Retorna os valores correntes de País, Estado e Cidade.
   *
   * Níveis não renderizados ainda podem participar do contexto e ser retornados
   * quando definidos externamente.
   *
   * @returns {{country:*,state:*,city:*}} Valor consolidado.
   */
  getValue() {
    return {
      country: this.#contextValue('country'),
      state: this.#contextValue('state'),
      city: this.#contextValue('city'),
    };
  }

  /**
   * Resolve o valor de um nível em um payload programático.
   *
   * @param {Object<string,*>} payload Payload recebido por `setValue()`.
   * @param {'country'|'state'|'city'} level Nível geográfico.
   * @returns {*} Valor resolvido.
   */
  #payloadValue(payload, level) {
    const field = this.fields[level];
    return readValue(payload, this.#candidateKeys(level))
      ?? readValue(payload, GEOGRAPHY_LEVELS[level].aliases)
      ?? field?.value
      ?? null;
  }

  /**
   * Atualiza programaticamente os valores e respeita a cascata geográfica.
   *
   * Um payload escalar é interpretado como país. Com `options.emit === true`,
   * emite `geographychange` ao final da atualização.
   *
   * @param {Object|*} [payload={}] Novos valores; um escalar é tratado como país.
   * @param {Object} [options={}] Opções da atualização (`emit`).
   * @returns {Promise.<Object>} Valor consolidado após a cascata.
   */
  async setValue(payload = {}, options = {}) {
    const value = payload && typeof payload === 'object' && !Array.isArray(payload)
      ? payload
      : { country: payload };

    const country = this.#payloadValue(value, 'country');
    const state = this.#payloadValue(value, 'state');
    const city = this.#payloadValue(value, 'city');

    if (hasValue(country)) {
      if (this.combos.country) {
        this.#selectCandidate('country', country);
      } else {
        this.external.country = country;
      }

      if (this.combos.state) await this.#refresh('state', state);
      else if (hasValue(state)) this.external.state = state;

      if (this.combos.city) await this.#refresh('city', city);
      else if (hasValue(city)) this.external.city = city;
    } else if (hasValue(state)) {
      if (this.combos.state) this.#selectCandidate('state', state);
      else this.external.state = state;

      if (this.combos.city) await this.#refresh('city', city);
      else if (hasValue(city)) this.external.city = city;
    } else if (hasValue(city)) {
      if (this.combos.city) this.#selectCandidate('city', city);
      else this.external.city = city;
    }

    if (options.emit === true) {
      this.root.dispatchEvent(new CustomEvent('geographychange', {
        bubbles: true,
        detail: { group: this.group, value: this.getValue() },
      }));
    }

    return this.getValue();
  }

  /**
   * Cria o root, os níveis habilitados e anexa a API pública.
   *
   * A API também fica disponível diretamente no root por `getValue()`,
   * `setValue()` e `getField()`.
   *
   * @returns {Promise.<Array.<HTMLElement|Object>>} Root e opções normalizadas para o pipeline EP.
   * @throws {Error} Quando `parent` não é encontrado ou nenhum nível é habilitado.
   */
  async create() {
    const parent = resolveParent(this.options.parent);
    if (!parent) throw new Error('Geography: parent não encontrado.');

    geographySequence += 1;
    const requestedGroup = String(this.options.group || this.options.id || '').trim();
    this.group = requestedGroup || `geography-${geographySequence}`;
    this.direction = String(this.options.direction || 'row').trim().toLowerCase() === 'col'
      ? 'col'
      : 'row';

    GEOGRAPHY_LEVEL_ORDER.forEach((level) => {
      this.fields[level] = this.#fieldConfig(level);
      this.initial[level] = this.#initialValue(level);
    });

    const enabledLevels = GEOGRAPHY_LEVEL_ORDER.filter((level) => this.#isEnabled(level));
    if (enabledLevels.length === 0) {
      throw new Error('Geography: habilite ao menos countries, states ou cities.');
    }

    this.root = document.createElement('div');
    this.root.classList.add('geography', 'grid', `geography-direction-${this.direction}`);
    this.root.dataset.geographyGroup = this.group;
    this.root.dataset.direction = this.direction;
    this.root.style.gridTemplateColumns = this.direction === 'col'
      ? 'minmax(0, 1fr)'
      : `repeat(${enabledLevels.length}, minmax(0, 1fr))`;

    append(this.root, {
      parent: this.options.parent,
      append: this.options.append,
    });

    const rootOptions = {
      id: this.options.id,
      class: this.options.class,
      style: this.options.style,
      row: this.options.row,
      col: this.options.col,
      rowspan: this.options.rowspan,
      colspan: this.options.colspan,
      aria: this.options.aria,
      data: {
        ...(this.options.data || {}),
        geographyGroup: this.group,
      },
    };

    setAttributes(this.root, rootOptions);
    setEvents(this.root, {
      ...rootOptions,
      click: this.options.click,
      focus: this.options.focus,
      blur: this.options.blur,
    });

    await this.#createFields();
    this.#bindApi();

    return [this.root, { ...this.options, parent: null }];
  }
}

/**
 * Cria um Geography declarativo.
 *
 * Se nenhuma das flags `countries`, `states` ou `cities` for informada, os três
 * níveis são habilitados. Se alguma flag for informada, somente os níveis com
 * valor estritamente `true` são criados.
 *
 * `value` e `values` podem usar os nomes configurados dos campos ou aliases:
 * `country`, `countryCode`, `country_code`, `state`, `stateCode`, `state_code`,
 * `uf`, `city`, `cityId` e `city_id`.
 *
 * O evento `geographychange` usa `detail` com `group`, `level` (quando originado
 * por interação) e `value` consolidado.
 *
 * @param {Object} [options={}] Configuração do componente.
 * @param {HTMLElement|string} options.parent Parent obrigatório.
 * @param {string} [options.id] Id do root.
 * @param {string} [options.group] Identificador lógico da instância.
 * @param {string} [options.name] Prefixo dos nomes padrão dos campos.
 * @param {'row'|'col'} [options.direction='row'] Direção estrutural dos níveis renderizados.
 * @param {boolean} [options.countries] Habilita País.
 * @param {boolean} [options.states] Habilita Estado.
 * @param {boolean} [options.cities] Habilita Cidade.
 * @param {Object|*} [options.value] Objeto completo ou escalar tratado como país.
 * @param {Object} [options.values] Objeto alternativo de binding.
 * @param {*} [options.defaultValue] Fallback do país.
 * @param {boolean} [options.disabled=false] Default de desabilitação dos níveis.
 * @param {boolean} [options.readOnly=false] Default de somente leitura.
 * @param {boolean} [options.readonly] Alias de compatibilidade para `readOnly`.
 * @param {Object} [options.fields] Configuração granular dos níveis.
 * @param {Object} [options.fields.country] Configuração do campo de País; aceita também `accept`, `reject` e `continentGroup`.
 * @param {Object} [options.fields.state] Configuração do campo de Estado/UF.
 * @param {Object} [options.fields.city] Configuração do campo de Cidade.
 * @returns {Promise.<Array.<HTMLElement|Object>>} Retorno normalizado do pipeline EP.
 *
 * @example
 * await create({
 *   element: 'geography',
 *   parent: '#form',
 *   name: 'address',
 *   countries: true,
 *   states: true,
 *   cities: true,
 *   accept: ['BR', 'US'],
 *   continentGroup: true,
 *   value: { country: 'BR', state: 'SP' }
 * });
 *
 * @see configureCountryCombo
 * @see geographyComboOptions
 */
export default async function geography(options = {}) {
  const instance = new Geography(options);
  return instance.create();
}
